/**
 * Inline Diff Preview (dev only)
 *
 * Mounts a single AuiSurface with one MarkdownEditor node, plus a panel of
 * preset "agent updates" that fire synthetic agent.run.surface_update events
 * through a fake PlatformEventContext. Exercises the full update path:
 * CanvasContext receives the event, auto-injects previousContent, and the
 * AuiMarkdownEditor swaps to MarkdownInlineDiff. Visit /dev/inline-diff-preview.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  PlatformEventContext,
  type SubscribeFn,
} from '@/contexts/PlatformEventContext';
import { CanvasProvider, useCanvases } from '@/contexts/CanvasContext';
import { AuiSurface } from '@/components/aui/AuiSurface';
import { Button } from '@/components/ui/button';
import type { PlatformEvent, EventFilter } from '@/types/events';
import type { Canvas, Surface } from '@/types/canvases';

const CONV_ID = 'conv-diff-preview';
const CANVAS_ID = 'cvs-doc';
const SURFACE_ID = 'srf-doc';
const NODE_ID = 'md-1';

const INITIAL_MD = `# Quarterly Product Update

A short summary of the work completed this quarter and what's planned next.

## Section 1 — Highlights

We shipped three major features and reduced p95 latency by 22%. The team focused on stability and observability heading into the holiday freeze.

## Section 2 — Metrics

- Active users: 12,400 (+8% QoQ)
- Conversion rate: 4.2%
- Average session: 6m 12s
- Crash rate: 0.04%

## Section 3 — Notes from Engineering

The platform team migrated the event bus to the new shared infrastructure. Sandbox runs now spin up in under two seconds. We are tracking a small number of regressions in the sandbox UI that will land next sprint.

## Section 4 — Deploy command

\`\`\`bash
npm run deploy:prd -- --confirm
\`\`\`
`;

const EDIT_PARA_S3 = INITIAL_MD.replace(
  'The platform team migrated the event bus to the new shared infrastructure. Sandbox runs now spin up in under two seconds. We are tracking a small number of regressions in the sandbox UI that will land next sprint.',
  'The platform team migrated the event bus to the new shared infrastructure, including abc. Sandbox runs now spin up in under two seconds, and abc telemetry has been added to the dashboards.',
);

const ADD_SECTION = `${INITIAL_MD}
## Section 5 — Looking Ahead

Next quarter we focus on reliability under spike load and finishing the multi-tenant analytics rollout. A new pricing experiment kicks off in week 2.
`;

const REMOVE_SECTION = INITIAL_MD.replace(
  /## Section 2 — Metrics\n\n- Active users: 12,400 \(\+8% QoQ\)\n- Conversion rate: 4\.2%\n- Average session: 6m 12s\n- Crash rate: 0\.04%\n\n/,
  '',
);

const LIST_EDIT = INITIAL_MD.replace(
  '- Active users: 12,400 (+8% QoQ)\n- Conversion rate: 4.2%\n- Average session: 6m 12s\n- Crash rate: 0.04%',
  '- Active users: 13,100 (+14% QoQ)\n- Conversion rate: 4.6%\n- Average session: 6m 38s\n- Crash rate: 0.03%\n- New: paid retention at 30d: 71%',
).replace(
  'We shipped three major features and reduced p95 latency by 22%. The team focused on stability and observability heading into the holiday freeze.',
  'We shipped four major features and reduced p95 latency by 28%. The team focused on stability, observability, and migration readiness heading into the holiday freeze.',
);

const REPLACE_CODE = INITIAL_MD.replace(
  '```bash\nnpm run deploy:prd -- --confirm\n```',
  '```python\nfrom optr.deploy import release\nrelease(env="prd", confirm=True)\n```',
);

interface Preset {
  label: string;
  description: string;
  content: string;
}

const PRESETS: Preset[] = [
  {
    label: 'Edit paragraph in section 3',
    description: 'Replaces the engineering paragraph with one that includes "abc".',
    content: EDIT_PARA_S3,
  },
  {
    label: 'Add a new section',
    description: 'Appends "Section 5 — Looking Ahead" with a paragraph.',
    content: ADD_SECTION,
  },
  {
    label: 'Remove section 2',
    description: 'Drops the "Metrics" heading and its bullet list.',
    content: REMOVE_SECTION,
  },
  {
    label: 'Multi-line list edit',
    description: 'Updates the highlights paragraph and rewrites every metric bullet.',
    content: LIST_EDIT,
  },
  {
    label: 'Replace code block',
    description: 'Swaps the bash deploy snippet for a Python equivalent.',
    content: REPLACE_CODE,
  },
  {
    label: 'Reset to initial',
    description: 'Restores the original document content.',
    content: INITIAL_MD,
  },
];

interface FakeEventBus {
  ctx: React.ContextType<typeof PlatformEventContext>;
  fire: (event: PlatformEvent) => void;
}

// Captures handlers from CanvasContext.subscribe and exposes a synchronous
// fire() to deliver synthetic events. Mirrors the production filter semantics
// (eventType match) — anything more is unnecessary for the preview.
function useFakeEventBus(): FakeEventBus {
  const handlersRef = useRef<
    Array<{ filters: EventFilter[]; handler: (e: PlatformEvent) => void }>
  >([]);

  const subscribe = useCallback<SubscribeFn>((filters, handler) => {
    const entry = { filters, handler };
    handlersRef.current.push(entry);
    return () => {
      handlersRef.current = handlersRef.current.filter((h) => h !== entry);
    };
  }, []);

  const fire = useCallback((event: PlatformEvent) => {
    for (const { filters, handler } of handlersRef.current) {
      const match =
        filters.length === 0 ||
        filters.some(
          (f) => f.eventType === undefined || f.eventType === event.eventType,
        );
      if (match) handler(event);
    }
  }, []);

  const ctx = useMemo(
    () => ({
      subscribe,
      isConnected: true,
      diagnostics: {
        connectedSince: Date.now(),
        disconnectCount: 0,
        lastDisconnectReason: null,
        lastDisconnectTime: null,
        reconnectAttempts: 0,
      },
    }),
    [subscribe],
  );

  return { ctx, fire };
}

const initialSurface: Surface = {
  canvasId: CANVAS_ID,
  surfaceId: SURFACE_ID,
  runId: 'run-fake',
  title: 'Sample Document',
  version: 1,
  spec: {
    updates: [
      {
        id: 'root',
        component: {
          type: 'Column',
          children: [NODE_ID],
        },
      },
      {
        id: NODE_ID,
        component: {
          type: 'MarkdownEditor',
          props: {
            content: INITIAL_MD,
            editable: false,
          },
        },
      },
    ],
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const initialCanvas: Canvas = {
  canvasId: CANVAS_ID,
  conversationId: CONV_ID,
  title: 'Document',
  isDefault: true,
  layout: 'stack',
  createdAt: new Date().toISOString(),
};

function SurfaceMount() {
  const { surfaces } = useCanvases();
  const surface = surfaces.get(`${CANVAS_ID}:${SURFACE_ID}`);
  if (!surface) {
    return (
      <div className="rounded border border-border bg-card p-4 text-sm text-muted-foreground">
        Surface not loaded.
      </div>
    );
  }
  return <AuiSurface surface={surface} />;
}

export default function InlineDiffPreviewPage() {
  const { ctx, fire } = useFakeEventBus();
  const versionRef = useRef(initialSurface.version);
  const [lastFired, setLastFired] = useState<string | null>(null);

  const initial = useMemo(
    () => ({ canvas: initialCanvas, surfaces: [initialSurface] }),
    [],
  );

  const handlePreset = useCallback(
    (preset: Preset) => {
      versionRef.current += 1;
      const event: PlatformEvent = {
        eventId: `evt-${Date.now()}`,
        eventType: 'agent.run.surface_update',
        source: 'dev-preview',
        data: {
          conversationId: CONV_ID,
          runId: 'run-fake',
          agentId: 'agent-fake',
          canvasId: CANVAS_ID,
          surfaceId: SURFACE_ID,
          version: versionRef.current,
          title: 'Sample Document',
          iteration: versionRef.current,
          spec: {
            updates: [
              {
                id: NODE_ID,
                component: {
                  type: 'MarkdownEditor',
                  props: {
                    content: preset.content,
                    editable: false,
                  },
                },
              },
            ],
          },
        },
        metadata: { timestamp: new Date().toISOString() },
        orgId: 'org-dev',
      };
      fire(event);
      setLastFired(preset.label);
    },
    [fire],
  );

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border px-6 py-4">
        <h1 className="text-2xl font-bold">Inline Diff Preview</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Click a preset to fire a synthetic <code>agent.run.surface_update</code>.
          The MarkdownEditor swaps to a block-level inline diff (red strikethrough
          for removed blocks, green for added). Dev only.
        </p>
      </header>
      <div className="flex flex-1 overflow-hidden">
        <aside className="flex w-80 shrink-0 flex-col gap-2 overflow-y-auto border-r border-border p-4">
          <h2 className="text-sm font-semibold text-foreground">Agent updates</h2>
          {PRESETS.map((preset) => (
            <div key={preset.label} className="space-y-1">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
              <p className="text-xs text-muted-foreground">{preset.description}</p>
            </div>
          ))}
          {lastFired && (
            <p className="mt-3 text-xs text-muted-foreground">
              Last fired: <span className="font-medium text-foreground">{lastFired}</span>
            </p>
          )}
        </aside>
        <main className="flex-1 overflow-auto p-6">
          <PlatformEventContext.Provider value={ctx}>
            <CanvasProvider
              conversationId={CONV_ID}
              initialCanvas={initial}
              autoScrollOnUpdate={false}
            >
              <SurfaceMount />
            </CanvasProvider>
          </PlatformEventContext.Provider>
        </main>
      </div>
    </div>
  );
}
